import { useMemo } from 'react';
import { groupTaste, type ResultEntry } from '@filmsloth/shared';
import { useGenreLabel, useMovieTitle, useT } from '../i18n/index.js';
import { Poster } from '../components/Poster.js';
import { ShareRankingButton } from '../components/ShareRankingButton.js';

export function ResultsScreen({
  results,
  loading,
  error,
  onClose,
  onReload,
}: {
  results: ResultEntry[];
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onReload: () => void;
}) {
  const t = useT();
  const movieTitle = useMovieTitle();
  const genreLabel = useGenreLabel();
  const taste = useMemo(() => groupTaste(results), [results]);

  return (
    <div className="screen screen--wide">
      <div className="row row--between">
        <h1>{t('results.title')}</h1>
        <button type="button" className="btn btn--ghost" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
      <p className="muted small">{t('results.subtitle')}</p>

      {error && (
        <div className="banner banner--error row row--between">
          <span>{error}</span>
          <button type="button" className="btn btn--ghost" onClick={onReload}>
            {t('common.retry')}
          </button>
        </div>
      )}

      {!error && loading && results.length === 0 && <p className="muted">{t('common.loading')}</p>}

      {!error && !loading && results.length === 0 && <p className="muted">{t('results.empty')}</p>}

      {/* One line about the group rather than about any film: the part of the
          evening people talk about afterwards, and a hint for the next round's
          filters. */}
      {!error && taste ? (
        <div className="panel taste">
          {taste.favourites.length > 0 ? (
            <p>{t('taste.favourites', { genres: taste.favourites.map(genreLabel).join(', ') })}</p>
          ) : null}
          {taste.avoided.length > 0 ? (
            <p className="muted small">
              {t('taste.avoided', { genres: taste.avoided.map(genreLabel).join(', ') })}
            </p>
          ) : null}
        </div>
      ) : null}

      {!error ? <ShareRankingButton results={results} /> : null}

      {!error && results.length > 0 && (
        <div className="panel panel--flush">
          <div className="list">
            {results.map((entry, index) => {
              const score = entry.score;
              const scoreClass =
                score > 0 ? 'score score--positive' : score < 0 ? 'score score--negative' : 'score';
              const formattedScore = t('results.points', {
                count: score > 0 ? `+${score}` : score,
              });
              return (
                // A grid rather than the wrapping list row: rank, cover, title
                // and score stay on one line even on the narrowest phone, so
                // the score reads as the row's answer instead of a stray
                // line under it.
                <div className="list__item result-row" key={entry.movie.id}>
                  <span className="rank">{index + 1}</span>
                  <Poster movie={entry.movie} size="sm" />
                  <div className="result-row__text">
                    <div className="result-row__title">
                      {movieTitle(entry.movie)}
                      {entry.movie.year !== null ? ` (${entry.movie.year})` : ''}
                    </div>
                    <div className="small muted">
                      {t('results.votes', {
                        likes: entry.likes,
                        neutrals: entry.neutrals,
                        dislikes: entry.dislikes,
                      })}
                    </div>
                    {/* Who said what, not just how many: "who said no" is the
                        first question when a favourite sinks. The no line
                        comes first and stands out for that reason. */}
                    {entry.voters.dislikes.length > 0 && (
                      <div className="small result-row__voters result-row__voters--no">
                        {t('results.votersNo', { names: entry.voters.dislikes.join(', ') })}
                      </div>
                    )}
                    {entry.voters.likes.length > 0 && (
                      <div className="small muted result-row__voters">
                        {t('results.votersYes', { names: entry.voters.likes.join(', ') })}
                      </div>
                    )}
                    {entry.voters.neutrals.length > 0 && (
                      <div className="small muted result-row__voters">
                        {t('results.votersNeutral', { names: entry.voters.neutrals.join(', ') })}
                      </div>
                    )}
                    {entry.matched && (
                      <span className="badge badge--match">{t('results.matched')}</span>
                    )}
                  </div>
                  <span className={scoreClass}>{formattedScore}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
